import { useCallback, useRef, useEffect, useMemo } from 'react';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { point, bearing, destination, distance as turfDistance } from '@turf/turf';

/**
 * Хук для управления инструментами рисования на карте.
 * Инкапсулирует логику работы с MapboxDraw, включая кастомную логику
 * для привязки к углам при рисовании.
 *
 * @param {object} map - Экземпляр карты MapLibre.
 * @param {object} options - Опции.
 * @param {function} options.onFeatureCreate - Колбэк при создании нового объекта.
 * @param {function} options.onFeatureUpdate - Колбэк при обновлении геометрии существующего объекта.
 */
export const useDrawingTools = (map, { onFeatureCreate, onFeatureUpdate, onFeatureSelect, onToolDeactivate }) => {
  const drawRef = useRef(null);
   const onDrawCompleteCallbackRef = useRef(null);
   const currentDrawTypeRef = useRef(null);
   const isGeometryEditRef = useRef(false);
   const onFeatureSelectRef = useRef(onFeatureSelect);
   const onToolDeactivateRef = useRef(onToolDeactivate);

   useEffect(() => {
       onFeatureSelectRef.current = onFeatureSelect;
   }, [onFeatureSelect]);

   useEffect(() => {
       onToolDeactivateRef.current = onToolDeactivate;
   }, [onToolDeactivate]);

  // --- Логика для привязки к углам ---

  // Функция для вычисления географического азимута между двумя точками
  const calculateBearing = (p1, p2) => {
    return bearing(p1, p2);
  }

  // Функция для привязки угла к ближайшему значению, кратному 45 градусам
  const snapAngle = (angle) => {
    const snapAngle = 45;
    return Math.round(angle / snapAngle) * snapAngle;
  };

  // Кастомный режим для рисования линий с привязкой
  const drawLineStringWithAngleSnap = {
    ...MapboxDraw.modes.draw_line_string,
    onMouseMove(state, e) {
      // Вызываем оригинальный обработчик
      MapboxDraw.modes.draw_line_string.onMouseMove.call(this, state, e);

      if (!state.line || !e.originalEvent.shiftKey) return;

      const currentCoordinates = state.line.coordinates;
      if (currentCoordinates.length < 2) return;

      const prevPointCoords = currentCoordinates[currentCoordinates.length - 2];
      const prevPoint = point(prevPointCoords);
      const currentPoint = point([e.lngLat.lng, e.lngLat.lat]);

      let finalAngle;

      // Если это второй сегмент или более, считаем угол относительно предыдущего сегмента
      if (currentCoordinates.length > 2) {
        const p0 = point(currentCoordinates[currentCoordinates.length - 3]); // Точка перед предыдущей
        const p1 = prevPoint; // Предыдущая точка
        const p2 = currentPoint; // Текущая точка

        const bearingPrev = calculateBearing(p0, p1);
        const bearingCurr = calculateBearing(p1, p2);
        
        let relativeAngle = bearingCurr - bearingPrev;
        // Нормализуем угол в диапазон (-180, 180]
        if (relativeAngle <= -180) relativeAngle += 360;
        if (relativeAngle > 180) relativeAngle -= 360;

        const snappedRelativeAngle = snapAngle(relativeAngle);
        finalAngle = bearingPrev + snappedRelativeAngle;
      } else { // Для первого сегмента привязываемся к абсолютным углам (N, E, S, W)
        finalAngle = snapAngle(calculateBearing(prevPoint, currentPoint));
      }

      const distanceInKm = turfDistance(prevPoint, currentPoint, { units: 'kilometers' });
      const newCoords = destination(prevPoint, distanceInKm, finalAngle, { units: 'kilometers' }).geometry.coordinates;

      // Обновляем последнюю координату линии
      state.line.updateCoordinate(currentCoordinates.length - 1, newCoords[0], newCoords[1]);
    },
    onClick(state, e) {
        // Применяем логику привязки и при клике
        if (e.originalEvent.shiftKey && state.line.coordinates.length > 1) {
            const currentCoordinates = state.line.coordinates;

            let finalAngle;
            const p1Coords = currentCoordinates[currentCoordinates.length - 2];
            const p2Coords = [e.lngLat.lng, e.lngLat.lat];

            if (currentCoordinates.length > 2) {
                const p0Coords = currentCoordinates[currentCoordinates.length - 3];
                const bearingPrev = calculateBearing(point(p0Coords), point(p1Coords));
                const bearingCurr = calculateBearing(point(p1Coords), point(p2Coords));
                
                let relativeAngle = bearingCurr - bearingPrev;
                if (relativeAngle <= -180) relativeAngle += 360;
                if (relativeAngle > 180) relativeAngle -= 360;

                const snappedRelativeAngle = snapAngle(relativeAngle);
                finalAngle = bearingPrev + snappedRelativeAngle;
            } else {
                finalAngle = snapAngle(calculateBearing(point(p1Coords), point(p2Coords)));
            }

            const distanceInKm = turfDistance(point(p1Coords), point(p2Coords), { units: 'kilometers' });
            const newCoords = destination(point(p1Coords), distanceInKm, finalAngle, { units: 'kilometers' }).geometry.coordinates;

            // Обновляем координаты в событии перед вызовом стандартного обработчика
            e.lngLat = { lng: newCoords[0], lat: newCoords[1] };
        }

        // Вызываем оригинальный обработчик клика
        MapboxDraw.modes.draw_line_string.onClick.call(this, state, e);
    },
    onKeyUp(state, e) {
        // Завершаем рисование по Enter
        if (e.keyCode === 13 && state.line.isValid()) {
            this.changeMode('simple_select', { featureIds: [state.line.id] });
            return;
        }
        // Отменяем текущее рисование по Escape, но остаемся в режиме
        if (e.keyCode === 27) {
            // Удаляем то, что нарисовали
            state.line.coordinates.splice(0, state.line.coordinates.length);
            // "Перезапускаем" режим, чтобы начать с чистого листа
            this.changeMode('draw_line_string_with_angle_snap', {});
            return;
        }
    },
  };

  // Кастомный режим для рисования полигонов с привязкой
  const drawPolygonWithAngleSnap = {
    ...MapboxDraw.modes.draw_polygon,
    onMouseMove(state, e) {
        // Вызываем оригинальный обработчик
        MapboxDraw.modes.draw_polygon.onMouseMove.call(this, state, e);

        if (!state.polygon || !e.originalEvent.shiftKey) return;

        const currentCoordinates = state.polygon.coordinates[0];
        if (currentCoordinates.length < 2) return;

        const prevPointCoords = currentCoordinates[currentCoordinates.length - 2];
        const prevPoint = point(prevPointCoords);
        const currentPoint = point([e.lngLat.lng, e.lngLat.lat]);

        let finalAngle;

        // Если это второй сегмент или более, считаем угол относительно предыдущего сегмента
        if (currentCoordinates.length > 2) {
            const p0 = point(currentCoordinates[currentCoordinates.length - 3]);
            const p1 = prevPoint;
            const p2 = currentPoint;

            const bearingPrev = calculateBearing(p0, p1);
            const bearingCurr = calculateBearing(p1, p2);
            let relativeAngle = bearingCurr - bearingPrev;
            if (relativeAngle <= -180) relativeAngle += 360;
            if (relativeAngle > 180) relativeAngle -= 360;

            const snappedRelativeAngle = snapAngle(relativeAngle);
            finalAngle = bearingPrev + snappedRelativeAngle;
        } else { // Для первого сегмента привязываемся к абсолютным углам
            finalAngle = snapAngle(calculateBearing(prevPoint, currentPoint));
        }
        const distanceInKm = turfDistance(prevPoint, currentPoint, { units: 'kilometers' });
        const newCoords = destination(prevPoint, distanceInKm, finalAngle, { units: 'kilometers' }).geometry.coordinates;

        // Обновляем последнюю координату полигона
        state.polygon.updateCoordinate(`0.${currentCoordinates.length - 1}`, newCoords[0], newCoords[1]);
    },
    onClick(state, e) {
        // Применяем логику привязки и при клике
        if (e.originalEvent.shiftKey && state.polygon.coordinates[0].length > 1) {
            const currentCoordinates = state.polygon.coordinates[0];

            let finalAngle;
            const p1Coords = currentCoordinates[currentCoordinates.length - 2];
            const p2Coords = [e.lngLat.lng, e.lngLat.lat];

            if (currentCoordinates.length > 2) {
                const p0Coords = currentCoordinates[currentCoordinates.length - 3];
                const bearingPrev = calculateBearing(point(p0Coords), point(p1Coords));
                const bearingCurr = calculateBearing(point(p1Coords), point(p2Coords));
                
                let relativeAngle = bearingCurr - bearingPrev;
                if (relativeAngle <= -180) relativeAngle += 360;
                if (relativeAngle > 180) relativeAngle -= 360;

                const snappedRelativeAngle = snapAngle(relativeAngle);
                finalAngle = bearingPrev + snappedRelativeAngle;
            } else {
                finalAngle = snapAngle(calculateBearing(point(p1Coords), point(p2Coords)));
            }

            const distanceInKm = turfDistance(point(p1Coords), point(p2Coords), { units: 'kilometers' });
            const newCoords = destination(point(p1Coords), distanceInKm, finalAngle, { units: 'kilometers' }).geometry.coordinates;

            // Обновляем координаты в событии перед вызовом стандартного обработчика
            e.lngLat = { lng: newCoords[0], lat: newCoords[1] };
        }

        // Вызываем оригинальный обработчик клика
        MapboxDraw.modes.draw_polygon.onClick.call(this, state, e);
    },
    onKeyUp(state, e) {
        // Завершаем рисование по Enter
        if (e.keyCode === 13 && state.polygon.isValid()) {
            this.changeMode('simple_select', { featureIds: [state.polygon.id] });
            return;
        }
        // Отменяем текущее рисование по Escape, но остаемся в режиме
        if (e.keyCode === 27) {
            // Удаляем то, что нарисовали
            if (state.polygon) {
                this.deleteFeature([state.polygon.id], { silent: true });
            }
            // "Перезапускаем" режим, чтобы начать с чистого листа
            this.changeMode('draw_polygon_with_angle_snap', {});
        }
    },
  };

   // Кастомный режим для рисования точек
   const drawPointCustom = useMemo(() => {
     const originalDrawPoint = MapboxDraw.modes.draw_point;
     
     return {
       ...originalDrawPoint,
       
       onSetup(state) {
         // Создаем "черновик" точки и добавляем его в состояние, как в оригинальном режиме
         const point = this.newFeature({
           type: 'Feature',
           properties: state.properties || {},
           geometry: { type: 'Point', coordinates: [] }
         });
         this.addFeature(point);
         this.clearSelectedFeatures();
         this.updateUIClasses({ mouse: 'add' });
         this.activateUIButton('point');
         this.setActionableState({ trash: true });
         return { point }; // Возвращаем точку в состоянии
       },

       onClick(state, e) {
         // Обновляем координаты точки в состоянии
         e.originalEvent.stopPropagation(); // ВАЖНО: Предотвращаем "проваливание" клика на карту, чтобы не сработал выбор объекта под меткой.
         state.point.updateCoordinate(e.lngLat.lng, e.lngLat.lat);
         // Вызываем оригинальный onClick, который завершит рисование и создаст событие
         return originalDrawPoint.onClick.call(this, state, e);
       },
     };
   }, []);

  // Инициализация MapboxDraw при монтировании
  useEffect(() => {
    if (!map) return;

    const draw = new MapboxDraw({
      displayControlsDefault: false, // Мы будем управлять режимами сами
      controls: {},
      modes: {
        ...MapboxDraw.modes,
        'draw_line_string_with_angle_snap': drawLineStringWithAngleSnap,
        'draw_polygon_with_angle_snap': drawPolygonWithAngleSnap,
        'draw_point_custom': drawPointCustom, // Регистрируем наш новый режим
      },
      styles: [
        // Стили для активного состояния рисования
        {
          id: 'gl-draw-line',
          type: 'line',
          filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#D20C0C', 'line-dasharray': [0.2, 2], 'line-width': 2 },
        },
        {
          id: 'gl-draw-polygon-fill',
          type: 'fill',
          filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          paint: { 'fill-color': '#D20C0C', 'fill-outline-color': '#D20C0C', 'fill-opacity': 0.1 },
        },
        {
          id: 'gl-draw-polygon-stroke-active',
          type: 'line',
          filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#D20C0C', 'line-dasharray': [0.2, 2], 'line-width': 2 },
        },
        {
          id: 'gl-draw-vertex-point-active',
          type: 'circle',
          filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
          paint: { 'circle-radius': 5, 'circle-color': '#FFF', 'circle-stroke-width': 2, 'circle-stroke-color': '#D20C0C' },
        },
      ],
    });

    map.addControl(draw);
    drawRef.current = draw;

    // Включаем режим привязки к углам
    MapboxDraw.constants.modes.DRAW_LINE_STRING = 'draw_line_string';
    draw.options.snap = true;
    draw.options.guides = true;

    // Обработчик создания объекта
    const handleCreate = ({ features }) => {
      const isMeasurement = !!onDrawCompleteCallbackRef.current;
      const feature = { ...features[0] }; // Клонируем объект, чтобы избежать мутаций
      feature.properties = { ...feature.properties }; // Клонируем свойства

      // Генерируем UUID для нового объекта, если его нет
      if (!feature.id) {
          feature.id = crypto.randomUUID();
      }
      // Убедимся, что ID также есть в properties для консистентности
      feature.properties.id = feature.id;

      // Добавляем тип объекта, который использовался для рисования
      if (currentDrawTypeRef.current && !feature.properties.type) { // Добавляем тип, только если его еще нет
          feature.properties.type = currentDrawTypeRef.current;
      }

      // Если есть временный колбэк (для инструментов измерения), вызываем его
      if (isMeasurement) {
        onDrawCompleteCallbackRef.current(feature);
        // Для измерений НЕ удаляем геометрию, чтобы она оставалась видимой вместе с результатом.
        // Она будет очищена при деактивации инструмента.
      } else if (isGeometryEditRef.current) { // Если мы в режиме редактирования геометрии
        onFeatureUpdate(feature);
        // После обновления выходим из режима рисования
        setTimeout(() => drawRef.current?.changeMode('simple_select', {}), 0);
        isGeometryEditRef.current = false; // Сбрасываем флаг после завершения редактирования
      } else if (onFeatureCreate) { // Иначе, это создание нового объекта
        // Вызываем коллбэк, чтобы родительский компонент мог начать процесс редактирования.
        onFeatureCreate(feature);
        // Теперь все типы объектов завершаются одинаково (двойной клик или Enter),
        // поэтому дополнительная логика для точек не нужна.
      }
    };
    
    const handleUpdate = ({ features, action }) => {
        if (action === 'move' || action === 'change_coordinates') {
            const updatedFeature = features[0];
            if (onFeatureUpdate) {
                onFeatureUpdate(updatedFeature);
            }
            // После обновления выходим из режима редактирования
            setTimeout(() => drawRef.current?.changeMode('simple_select', {}), 0);
        }
    };

    map.on('draw.create', handleCreate);
    map.on('draw.update', handleUpdate);

    // Очистка при размонтировании
    return () => {
      map.off('draw.create', handleCreate);
      if (map.hasControl(drawRef.current)) {
        map.removeControl(drawRef.current);
      }
      drawRef.current = null;
      map.off('draw.update', handleUpdate);
    };
  }, [map, onFeatureCreate, onFeatureUpdate]);

  /**
   * Запускает режим рисования.
   * @param {string} mode - 'line' или 'polygon'.
   * @param {object} [defaultProperties] - Свойства по умолчанию для нового объекта.
   */
  const startDrawing = useCallback((mode, defaultProperties, isGeometryEdit = false) => {
    if (!drawRef.current) return;
    
    // Запоминаем тип объекта в ref. Это надежнее, чем state, для доступа из колбэков.
    currentDrawTypeRef.current = defaultProperties?.type || null;
    isGeometryEditRef.current = isGeometryEdit; // Устанавливаем флаг режима редактирования геометрии

    // Используем наши кастомные режимы
    let drawMode;
    if (mode === 'polygon') {
        drawMode = 'draw_polygon_with_angle_snap';
    } else if (mode === 'line') {
        drawMode = 'draw_line_string_with_angle_snap';
    } else { // 'point' для меток
        drawMode = 'draw_point_custom'; // Используем наш новый режим
    }
    drawRef.current.changeMode(drawMode, defaultProperties || {});
  }, []);

  /**
   * Устанавливает временный колбэк, который будет вызван при создании объекта.
   * @param {function | null} callback - Функция обратного вызова или null для сброса.
   */
  const setOnDrawComplete = useCallback((callback) => {
    onDrawCompleteCallbackRef.current = callback;
  }, []);

  /**
   * Останавливает режим рисования.
   */
  const stopDrawing = useCallback(() => {
    if (!drawRef.current) return;
    drawRef.current.changeMode('simple_select');
    // Не нужно удалять все, так как инструменты измерения могут захотеть
    // оставить свою геометрию на карте для отображения результатов.
    // Инструменты сами вызовут cleanup, который удалит временные слои.
    // drawRef.current.deleteAll(); 
  }, []);

  /**
   * Удаляет все нарисованные в данный момент геометрии из MapboxDraw.
   */
  const clearDrawing = useCallback(() => {
    if (drawRef.current) {
      drawRef.current.deleteAll();
    }
  }, []);

  /**
   * Очищает все ресурсы, связанные с рисованием.
   */
  const cleanup = useCallback(() => {
    if (map && drawRef.current && map.hasControl(drawRef.current)) {
      map.removeControl(drawRef.current);
    }
  }, [map]);

  /**
   * Запускает режим редактирования геометрии для существующего объекта.
   * @param {object} feature - GeoJSON объект для редактирования.
   */
  const startEditing = useCallback((feature) => {
      if (!drawRef.current) return;
      drawRef.current.deleteAll(); // Очищаем предыдущие
      drawRef.current.add(feature);
      drawRef.current.changeMode('direct_select', { featureId: feature.id });
  }, []);

  /**
   * Останавливает режим редактирования геометрии.
   */
  const stopEditing = useCallback(() => {
      if (!drawRef.current) return;
      drawRef.current.changeMode('simple_select');
      drawRef.current.deleteAll();
  }, []);

  return { startDrawing, stopDrawing, cleanup, setOnDrawComplete, clearDrawing, startEditing, stopEditing };
};